import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile bot check (https://developers.cloudflare.com/turnstile/).
 *
 * The backend checks the token before sending an OTP to a number that has no
 * account yet - the only route an SMS-pumping bot can use to spend money - and
 * in "enforce_browsers" mode it refuses browser requests that carry none.
 * Existing customers are never challenged server-side.
 *
 * "interaction-only" keeps the widget invisible for most people. Tokens are
 * single-use: change `resetKey` after every request that consumed one.
 *
 * With REACT_APP_TURNSTILE_SITE_KEY unset this renders nothing and callers send
 * no token, which the backend accepts in monitor mode.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined" || window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null; // allow a retry on the next mount
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export const Turnstile = ({ onToken, resetKey, action, className }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  });

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          appearance: "interaction-only",
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
        });
      })
      // Blocked script or no network: carry on without a token and let the
      // backend decide, rather than trap the user on this screen.
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [action]);

  useEffect(() => {
    if (resetKey === undefined || !widgetIdRef.current || !window.turnstile) return;
    onTokenRef.current(null);
    window.turnstile.reset(widgetIdRef.current);
  }, [resetKey]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
};

export default Turnstile;
